/** Email outbox evidence; a Hub notification alone is not an email receipt. */
export interface NoteEmailDelivery {
  total: number;
  queued: number;
  sending: number;
  sent: number;
  delivered: number;
  failed: number;
  bounced: number;
  complained: number;
}

export function emptyNoteEmailDelivery(): NoteEmailDelivery {
  return { total: 0, queued: 0, sending: 0, sent: 0, delivered: 0, failed: 0, bounced: 0, complained: 0 };
}

/**
 * Who a published lesson note is emailed to.
 *
 * The rule that decides which adults receive what a tutor wrote about a child,
 * so it is pure, tested on its own, and deliberately narrow: it sends only to
 * addresses already on the student's own record. It invents nothing, follows no
 * family links, and an address nobody recorded receives nothing.
 *
 * ## On `email_consent`
 *
 * The column exists and is deliberately not consulted. Two reasons.
 *
 * A lesson note is service communication about a lesson the family is paying
 * for — the system this replaces emailed notes as they were written, and that
 * is what families expect. It is not marketing, and consent is not the thing
 * that makes it lawful.
 *
 * More practically, the column cannot carry the distinction that would matter.
 * It is `not null default false`, and only the legacy import ever writes it, so
 * "asked and said no" and "never asked" are the same value. Roughly half the
 * students — everyone created by `adopt-bookers` — are false purely by default.
 * Gating on it would silently stop notes for them while looking principled.
 *
 * If opting out ever needs recording, that needs three states and somewhere to
 * set them, not this column.
 */

export interface LessonNotePart { text: string; href?: string }

/** Unicode whitespace plus invisible paste markers. Shared with SQL's btrim
 * character list so an empty write-up means the same thing in every locale. */
export const LESSON_NOTE_BLANK_CHARACTERS = "\u0009\u000a\u000b\u000c\u000d\u0020\u0085\u00a0\u00ad\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u200b\u200c\u200d\u2028\u2029\u202f\u205f\u2060\u3000\ufeff";
const lessonNoteTextPattern = new RegExp(`[^${LESSON_NOTE_BLANK_CHARACTERS}]`, "u");

export function hasLessonNoteText(content: string | null | undefined): boolean {
  return lessonNoteTextPattern.test(content ?? "");
}

/** Restrict inline references to durable Hub resource routes, never raw HTML. */
export function safeLessonNoteHref(href: string): boolean {
  return /^\/(?:help\/[a-z0-9]+(?:-[a-z0-9]+)*|g\/[a-zA-Z0-9%_-]+|public\/grooves\/[a-zA-Z0-9%_-]+|api\/lesson-resources\/(?:private|school)\/[a-zA-Z0-9%_-]+\/assets\/[a-zA-Z0-9%_-]+\/open)$/.test(href);
}

export function lessonNoteLink(title: string, href: string): string {
  if (!safeLessonNoteHref(href)) throw new Error("That resource link is not a Hub reference.");
  const label = title.replace(/[\r\n]+/g, " ").replace(/[\\\[\]]/g, "\\$&");
  return `[${label}](${href})`;
}

export function lessonNoteParts(content: string): LessonNotePart[] {
  const parts: LessonNotePart[] = [];
  const pattern = /\[((?:\\.|[^\]\\\n])*)\]\(([^\s()]+)\)/g;
  let start = 0;
  for (const match of content.matchAll(pattern)) {
    if (!safeLessonNoteHref(match[2]!)) continue;
    if (match.index > start) parts.push({ text: content.slice(start, match.index) });
    parts.push({ text: match[1]!.replace(/\\([\\\[\]])/g, "$1"), href: match[2]! });
    start = match.index + match[0].length;
  }
  if (start < content.length) parts.push({ text: content.slice(start) });
  return parts;
}

/** Remove one deleted resource's references without rewriting surrounding notes. */
export function removeLessonNoteLinks(content: string, href: string): string {
  if (!safeLessonNoteHref(href)) return content;
  return content.replace(/\[((?:\\.|[^\]\\\n])*)\]\(([^\s()]+)\)/g, (match, label: string, target: string) =>
    target === href ? label.replace(/\\([\\\[\]])/g, "$1") : match,
  );
}

export function lessonNotePlainText(content: string, baseUrl: string): string {
  return lessonNoteParts(content).map((part) => part.href
    ? `${part.text} (${new URL(part.href, baseUrl).href})` : part.text).join("");
}

export interface NoteAudience {
  /** Anything other than "active" gets nothing: a leaver is not a recipient. */
  status: string;
  studentEmail: string | null;
  parentEmail: string | null;
  secondaryEmail: string | null;
}

function usable(value: string | null): string | null {
  const trimmed = (value ?? "").trim().toLowerCase();
  return trimmed === "" ? null : trimmed;
}

/**
 * Every address on the record, in the order they should be tried, without
 * duplicates.
 *
 * Deduplicated because an adult who books for themselves usually has the same
 * address in both `student_email` and `parent_email` — every student created
 * by `adopt-bookers` does — and mailing them the same note twice reads as a
 * fault in the system rather than as thoroughness.
 *
 * An empty result is a real answer, not a failure: it means the school holds
 * no address for this student, which is a gap worth seeing rather than an
 * error worth throwing.
 */
export function noteRecipients(
  student: NoteAudience,
  /**
   * Addresses added by a parent, a tutor or the owner — see
   * `student_note_recipients`. Appended rather than replacing: an addition is
   * an addition, and nobody adding one intends to stop the notes reaching
   * whoever already received them.
   */
  extra: readonly string[] = [],
): string[] {
  if (student.status !== "active") return [];

  const seen = new Set<string>();
  for (const address of [
    student.parentEmail,
    student.studentEmail,
    student.secondaryEmail,
    ...extra,
  ]) {
    const usableAddress = usable(address);
    if (usableAddress) seen.add(usableAddress);
  }
  return [...seen];
}

/**
 * How much of a School pupil's family a published School note is emailed to.
 *
 * Three states rather than a boolean, because the question this answers was
 * never "on or off". A School payer is frequently the school's own finance
 * office rather than a parent, so the widening had to name whose addresses it
 * uses rather than inheriting "whoever pays" from the private side.
 *
 *  - `off` — nobody is emailed. The default, and the state the School domain
 *    shipped in while who should receive one was undecided.
 *  - `pupil` — `school_pupils.email` and nothing else. The address the school
 *    itself holds for the child, usually their school-issued one.
 *  - `family` — that address *and* the ones on the bridged private record:
 *    the parent, the pupil's own personal address, a second household, and
 *    anything a parent or tutor asked us to add.
 *
 * `family` is a superset of `pupil`, deliberately. Switching up must not be
 * able to stop a note reaching somebody who was already receiving it.
 */
export type SchoolNoteEmailPolicy = "off" | "pupil" | "family";

export interface SchoolNoteAudience {
  /** `school_pupils.email` — what the School domain holds for the child. */
  pupilEmail: string | null;
  /**
   * The approved bridged private record, or null when the pupil has none.
   *
   * Null is an ordinary state and not a failure. An unbridged pupil has no
   * family on the private side to reach, so under `family` they are reached at
   * their School address alone — which is exactly what `pupil` would have done
   * and never less.
   */
  family: NoteAudience | null;
  /** `student_note_recipients` on that bridged record, if any. */
  extra?: readonly string[];
}

/**
 * Every address a published School note should go to, deduplicated.
 *
 * Pure, so the decision that widens who reads what a tutor wrote about a child
 * can be read in one place and tested without a database.
 *
 * The pupil's School address comes first, so `family` reads as `pupil` plus
 * the household rather than as a different audience. Everything after it is
 * `noteRecipients`' own order — parent, pupil's personal address, second
 * household, additions — because the two sides answering "who gets a note" the
 * same way is the point of routing both through this file.
 *
 * A leaver on the private record contributes nothing, which `noteRecipients`
 * already enforces. The School pupil's own address is not covered by that rule
 * and does not need to be: the caller only reaches an active pupil.
 */
export function schoolNoteRecipients(
  policy: SchoolNoteEmailPolicy,
  audience: SchoolNoteAudience,
): string[] {
  if (policy === "off") return [];

  const seen = new Set<string>();
  const pupil = usable(audience.pupilEmail);
  if (pupil) seen.add(pupil);
  if (policy === "pupil") return [...seen];

  if (audience.family) {
    for (const address of noteRecipients(audience.family, audience.extra ?? [])) {
      seen.add(address);
    }
  }
  return [...seen];
}
